<template>
  <div id="login-security" class="fill-height align-start">
    <v-dialog
      v-if="changePasswordDialog"
      v-model="changePasswordDialog"
      width="400px"
      @click:outside="changePasswordDialog = false"
      @keydown.esc="changePasswordDialog = false"
    >
      <i-confirmation-dialog
        :text="'Upon password update, you will be immediately redirected to the login page.'"
        :action-text="'Confirm'"
        :submitting="updatingPassword"
        @cancel="changePasswordDialog = false"
        @confirm="submitPassword()"
      />
    </v-dialog>

    <v-row class="g-container" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0 text-center">{{ $route.meta.title }}</h2>
      </v-col>

      <v-row no-gutters>
        <v-col cols="12">
          <v-card
            class="p-update-card mx-auto rounded-lg mt-2 mt-md-4 pa-4 px-md-10 py-md-6 mb-8"
          >
            <v-form
              ref="page-content-form"
              v-model="passwordFormState"
              :disabled="!editPasswordForm"
              @submit.prevent
            >
              <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                <span class="g-heading-s g-bold">
                  Login Password
                  <v-icon
                    class="pb-1"
                    color="primary"
                    small
                    v-tooltip.right="'This is the Password used to login'"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </span>
              </v-card-text>

              <v-row>
                <v-col class="py-0" cols="12">
                  <v-text-field
                    autocomplete="off"
                    dense
                    outlined
                    required
                    v-model="currentPassword"
                    background-color="#F0F0F0"
                    label="Current Password *"
                    :append-icon="
                      showCurrentPassword ? 'mdi-eye' : 'mdi-eye-off'
                    "
                    :rules="rulesCurrentPasswordRequired"
                    :type="showCurrentPassword ? 'text' : 'password'"
                    @click:append="showCurrentPassword = !showCurrentPassword"
                  ></v-text-field>
                </v-col>

                <v-col class="py-0" cols="12" md="6" v-if="editPasswordForm">
                  <v-text-field
                    autocomplete="off"
                    dense
                    outlined
                    required
                    v-model="password"
                    background-color="#F0F0F0"
                    label="New Password *"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :rules="passwordRules"
                    :type="showPassword ? 'text' : 'password'"
                    @click:append="showPassword = !showPassword"
                    @focus="passwordValidator = true"
                  ></v-text-field>

                  <div
                    v-if="passwordValidator && editPasswordForm"
                    class="mt-n4 mt-md-0 mb-8 ml-2"
                    cols="12"
                  >
                    <i-password-criteria :password="password" />
                  </div>
                </v-col>

                <v-col class="py-0" cols="12" md="6" v-if="editPasswordForm">
                  <v-text-field
                    autocomplete="off"
                    dense
                    outlined
                    required
                    v-model="rePassword"
                    background-color="#F0F0F0"
                    label="Re-type New Password *"
                    :append-icon="showRePassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :rules="[passwordsMatchRule]"
                    :type="showRePassword ? 'text' : 'password'"
                    @click:append="showRePassword = !showRePassword"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-card-actions class="pa-0 justify-center">
                <v-spacer v-if="!$vuetifyDisplay.smAndDown"> </v-spacer>
                <template v-if="!editPasswordForm">
                  <v-btn
                    rounded
                    class="i-btn-block-secondary"
                    @click="editPasswordForm = !editPasswordForm"
                  >
                    Edit
                  </v-btn>
                </template>
                <template v-else>
                  <v-btn
                    rounded
                    text
                    class="i-btn-text-black"
                    @click="resetPasswordForm()"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    rounded
                    class="i-btn-block-secondary"
                    :loading="updatingPassword"
                    :disabled="!passwordFormState"
                    @click="changePasswordDialog = true"
                  >
                    Confirm
                  </v-btn>
                </template>
              </v-card-actions>
            </v-form>
          </v-card>

          <v-card
            class="p-update-card mx-auto rounded-lg mt-2 mt-md-4 pa-4 px-md-10 py-md-6 mb-8"
          >
            <v-form
              ref="page-content-form"
              v-model="emailFormState"
              :disabled="!editEmailForm"
              @submit.prevent
            >
              <template v-if="!userIsAllowedToEdit">
                <v-alert
                  dense
                  text
                  outlined
                  border="left"
                  elevation="1"
                  type="info"
                >
                  <div class="text-center">
                    Contact <strong>Account Owner</strong> to update login email
                    address
                  </div>
                </v-alert>

                <v-divider class="mt-6 mx-0" horizontal></v-divider>
              </template>

              <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                <span class="g-heading-s g-bold">
                  Login Email Address
                  <v-icon
                    class="pb-1"
                    color="primary"
                    small
                    v-tooltip.right="'This is the Email used to login'"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </span>
              </v-card-text>

              <v-row>
                <v-col class="py-0" cols="12">
                  <v-text-field
                    outlined
                    required
                    background-color="#F0F0F0"
                    dense
                    label="Email Address *"
                    :value="mutableEmail"
                    :loading="checkingEmail"
                    :error-messages="emailErrorMessage"
                    :rules="rulesEmail"
                    @input="(email) => checkEmailExistsDebounce(email)"
                  ></v-text-field>
                </v-col>

                <v-col class="py-0" cols="12" v-if="editEmailForm">
                  <v-text-field
                    autocomplete="off"
                    dense
                    outlined
                    required
                    v-model="currentPassword"
                    background-color="#F0F0F0"
                    label="Current Password *"
                    :append-icon="
                      showCurrentPasswordInEmailForm ? 'mdi-eye' : 'mdi-eye-off'
                    "
                    :rules="rulesCurrentPasswordRequired"
                    :type="showCurrentPasswordInEmailForm ? 'text' : 'password'"
                    @click:append="
                      showCurrentPasswordInEmailForm =
                        !showCurrentPasswordInEmailForm
                    "
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-card-actions
                v-if="userIsAllowedToEdit"
                class="pa-0 justify-center"
              >
                <v-spacer v-if="!$vuetifyDisplay.smAndDown"></v-spacer>
                <template v-if="!editEmailForm">
                  <v-btn
                    rounded
                    class="i-btn-block-secondary"
                    @click="editEmailForm = !editEmailForm"
                  >
                    Edit details
                  </v-btn>
                </template>
                <template v-else>
                  <v-btn
                    rounded
                    text
                    class="i-btn-text-black"
                    @click="resetEmailForm()"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    rounded
                    class="i-btn-block-secondary"
                    :loading="updatingEmail"
                    :disabled="!isNewEmail || !emailFormState"
                    @click="submitEmail()"
                  >
                    Confirm
                  </v-btn>
                </template>
              </v-card-actions>
            </v-form>
          </v-card>

          <v-card
            class="p-update-card mx-auto rounded-lg pa-4 px-md-10 py-md-6"
          >
            <v-form
              ref="page-content-form"
              v-model="accountDetailsFormState"
              :disabled="!editAccountDetailsForm"
              @submit.prevent
            >
              <template v-if="!userIsAllowedToEdit">
                <v-alert
                  dense
                  text
                  outlined
                  border="left"
                  elevation="1"
                  type="info"
                >
                  <div class="text-center">
                    Contact <strong>Account Owner</strong> to update details
                  </div>
                </v-alert>

                <v-divider class="mt-6 mx-0" horizontal></v-divider>
              </template>

              <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                <span class="g-heading-s g-bold"> User Details </span>
              </v-card-text>

              <v-row>
                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    outlined
                    required
                    background-color="#F0F0F0"
                    label="* First Name (required)"
                    v-model="mutableAccountDetails.firstName"
                    :rules="rulesFirstName"
                  ></v-text-field>
                </v-col>

                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    outlined
                    required
                    background-color="#F0F0F0"
                    label="* Last Name (required)"
                    v-model="mutableAccountDetails.lastName"
                    :rules="rulesLastName"
                  ></v-text-field>
                </v-col>

                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    dense
                    outlined
                    required
                    background-color="#F0F0F0"
                    label="* Mobile or Landline (required)"
                    hint="Add Area Code if Landline"
                    v-model="mutableAccountDetails.mobile"
                    :rules="rulesPhone"
                  ></v-text-field>
                </v-col>

                <v-col class="py-0" cols="12" md="6">
                  <v-select
                    attach
                    background-color="#F0F0F0"
                    dense
                    :items="businessDepartments"
                    label="* Department (Required)"
                    outlined
                    required
                    :rules="[(v) => !!v || 'Department required']"
                    v-model="mutableAccountDetails.department"
                  >
                  </v-select>
                </v-col>
              </v-row>

              <v-card-actions
                v-if="userIsAllowedToEdit"
                class="pa-0 justify-center"
              >
                <v-spacer v-if="!$vuetifyDisplay.smAndDown"></v-spacer>
                <template v-if="!editAccountDetailsForm">
                  <v-btn
                    rounded
                    class="i-btn-block-secondary"
                    @click="editAccountDetailsForm = true"
                  >
                    Edit details
                  </v-btn>
                </template>
                <template v-else>
                  <v-btn
                    rounded
                    text
                    class="i-btn-text-black"
                    @click="resetAccountDetailsForm()"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    rounded
                    class="i-btn-block-secondary"
                    :loading="updatingAccountDetails"
                    :disabled="!isNewAccountDetails || !accountDetailsFormState"
                    @click="submitAccountDetailsForm()"
                  >
                    <span>Confirm</span>
                  </v-btn>
                </template>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-col>
      </v-row>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  updateDetails,
  updateEmail,
  updatePassword
} from '@/services/credentials'
import { checkEmailExists } from '@/services/profile'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import BusinessDepartments from '@/utils/BusinessDepartments'
import FieldValidators from '@/utils/FieldValidators'
import PasswordHelper from '@/components/helpers/PasswordHelper'
import iPasswordCriteria from '@/components/PasswordCriteria.vue'

const DEBOUNCE_TIMER = 1000

export default {
  components: {
    iConfirmationDialog,
    iPasswordCriteria
  },

  data: () => ({
    accountDetailsFormState: false,
    businessDepartments: BusinessDepartments,
    changePasswordDialog: false,
    checkingEmail: false,
    currentPassword: null,
    debounceEmail: null,
    editAccountDetailsForm: false,
    editEmailForm: false,
    editPasswordForm: false,
    emailErrorMessage: null,
    emailFormState: false,
    mutableEmail: undefined,
    mutableAccountDetails: {
      firstName: '',
      lastName: '',
      mobile: '',
      department: ''
    },
    password: '',
    passwordFormState: false,
    passwordValidator: false,
    rePassword: '',
    showCurrentPassword: false,
    showCurrentPasswordInEmailForm: false,
    showPassword: false,
    showRePassword: false,
    updatingEmail: false,
    updatingPassword: false,
    updatingAccountDetails: false,
    ...FieldValidators
  }),

  computed: {
    ...mapState('auth', ['email', 'contact']),
    ...mapGetters('preferences', ['isOwnerUser']),

    isNewAccountDetails() {
      return !_.isEqual(this.mutableAccountDetails, this.contact)
    },

    isValidEmail() {
      return !!this.mutableEmail && /.+@.+/.test(this.mutableEmail)
    },

    passwordRules() {
      const rules = PasswordHelper.passwordRules()
      rules.push(
        PasswordHelper.passwordNotSameAsCurrentRule(
          this.currentPassword,
          this.password
        )
      )
      return rules
    },

    passwordsMatchRule() {
      return PasswordHelper.passwordsMatchRule(this.password, this.rePassword)
    },

    isNewEmail() {
      return this.mutableEmail !== this.email
    },

    userIsAllowedToEdit() {
      return this.isOwnerUser
    }
  },

  methods: {
    ...mapActions('auth', [
      'updateAccountDetails',
      'updateSupplierEmail',
      'logout'
    ]),
    ...mapActions('notifications', ['addNotification']),

    // **
    // Helper functions
    // **

    checkEmailExistsDebounce(email) {
      this.mutableEmail = email

      // Only validate in backend once has passed frontend validations
      if (this.isValidEmail) {
        clearTimeout(this.debounceEmail)
        this.debounceEmail = setTimeout(() => {
          this.checkingEmail = true
          this.emailErrorMessage = 'Validating Email'

          checkEmailExists({ email })
            .then((res) => {
              this.emailErrorMessage = res ? 'Email already registered' : null
            })
            .catch((err) => {
              // Validation APIs have request limiters
              const errMessage =
                err.response.status === 429
                  ? 'We detected too many Email field validation attempts. Please wait a few minutes before trying again.'
                  : 'Email validation not completed, try again...'

              this.addNotification({
                color: 'error',
                text: errMessage,
                timeout: -1
              })
            })
            .finally(() => {
              this.checkingEmail = false
            })
        }, DEBOUNCE_TIMER)
      }
    },

    // **
    // Related to Account Details form
    // **

    resetAccountDetailsForm() {
      this.editAccountDetailsForm = false
      this.mutableAccountDetails = { ...this.contact }
    },

    submitAccountDetailsForm() {
      this.updatingAccountDetails = true

      const request = { ...this.mutableAccountDetails }

      updateDetails(request)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'User Details updated successfully'
          })
          this.updateAccountDetails(request)
          this.resetAccountDetailsForm()
        })
        .catch((err) => {
          console.log(err)
          this.addNotification({
            color: 'error',
            text: 'User Details could not be updated, please try again...'
          })
        })
        .finally(() => {
          this.updatingAccountDetails = false
        })
    },

    // **
    // Related to Email form
    // **

    resetEmailForm() {
      this.editEmailForm = false
      this.currentPassword = null
      this.mutableEmail = this.email
    },

    submitEmail() {
      this.updatingEmail = true

      const request = {
        currentPassword: this.currentPassword,
        newEmail: this.mutableEmail
      }

      updateEmail(request)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Email successfully updated'
          })
          this.updateSupplierEmail(this.mutableEmail)
          this.resetEmailForm()
          this.logout()
        })
        .catch((err) => {
          let error
          switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
            case 'WRONG_PASSWORD':
              error = 'Current Password is incorrect'
              break

            case 'EMAIL_EXISTS':
              error = 'Email already exists'
              break

            default:
              error = 'Unknown error'
          }
          this.addNotification({ color: 'error', text: error })
        })
        .finally(() => {
          this.updatingEmail = false
        })
    },

    // **
    // Related to Password form
    // **

    resetPasswordForm() {
      this.editPasswordForm = false
      this.currentPassword = this.password = this.rePassword = ''
    },

    submitPassword() {
      this.updatingPassword = true

      const request = {
        currentPassword: this.currentPassword,
        newPassword: this.password
      }

      updatePassword(request)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Password successfully updated'
          })
          this.resetPasswordForm()
          this.logout()
        })
        .catch((err) => {
          let error

          switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
            case 'WRONG_PASSWORD':
              error = 'Current Password is incorrect'
              break

            case 'NEW_PASSWORD_SAME_AS_CURRENT':
              error = 'New Password must not be the same as current'
              break

            case 'PASSWORD_TOO_WEAK':
              error = 'Password is too weak'
              break

            case 'PASSWORD_TOO_COMMON':
              error = 'Password is too common'
              break

            case 'PASSWORD_CONTAINS_USER_INFO':
              error = 'Password contains some of your information'
              break

            default:
              error = 'Unknown error'
          }
          this.addNotification({ color: 'error', text: error })
        })
        .finally(() => {
          this.updatingPassword = false
        })
    }
  },

  mounted() {
    this.resetAccountDetailsForm()
    this.resetEmailForm()
    this.resetPasswordForm()
  }
}
</script>

<style lang="scss" scoped>
#login-security {
  hr {
    min-width: 100%;
    margin-top: 10px;
    margin-bottom: 30px;
  }
  .p-update-card {
    max-width: 740px;
  }
  .mdi {
    color: black;
  }
}
</style>
